import React, { useEffect } from "react";
import Modal from "Components/Common/GenericModal";
import Button from "Components/Common/Button";
import Pills from "Components/Common/Pills";
import "./styles.scss";
import { useMutation } from "@tanstack/react-query";
import {
  GenerateNewOtp,
  WithdrawGuestAccess,
} from "endpoints/guestAccess/index.action";
import { toast } from "react-toastify";

interface ViewGuestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  guest?: any;
  refetchRequests: () => void;
  setAccessData: React.Dispatch<React.SetStateAction<any>>;
}

const formatActiveTill = (s?: string) => s ?? "-";

const ViewGuestDetailsModal: React.FC<ViewGuestDetailsModalProps> = ({
  isOpen,
  onClose,
  guest,
  refetchRequests,
  setAccessData,
}) => {
  const [otp, setOtp] = React.useState<string>("");

  useEffect(() => {
    if (guest?.otp) {
      setOtp(guest.otp);
    }
  }, [guest]);
  const generateOtpMutation = useMutation({
    mutationKey: ["generate-otp"],
    mutationFn: async () => {
      const response = await GenerateNewOtp(guest.id);
      if (response && response?.statusCode === 200) {
        setOtp(response.data.code);
        // refetchRequests();
        setAccessData((prevData: any) => {
          const updatedRequests = prevData?.map((request: any) => {
            if (request.id === guest.id) {
              return {
                ...request,
                otp: response.data.code,
              };
            }
            return request;
          });
          return [...updatedRequests];
        });
        // onClose();
        toast.success("New O.T.P generated successfully");
      } else {
        throw new Error(
          response?.message ||
            response?.response ||
            "Failed to generate new O.T.P"
        );
      }
      return response;
    },
    onError: (error: any) => {
      toast.error(
        error?.message || "An error occurred while generating new O.T.P"
      );
    },
  });
  const withdrawMutation = useMutation({
    mutationKey: ["withdraw-guest-access"],
    mutationFn: async () => {
      const response = await WithdrawGuestAccess(guest.id);
      if (response && response?.statusCode === 200) {
        refetchRequests();
        onClose();
        toast.success("Guest access withdrawn successfully");
      } else {
        throw new Error(
          response?.message ||
            response?.response ||
            "Failed to withdraw guest access"
        );
      }
      return response;
    },
    onError: (error: any) => {
      toast.error(
        error?.message || "An error occurred while withdrawing guest access"
      );
    },
  });
  const title = (
    <div className="view-guest-header">
      <div className="view-guest-icon-wrapper">
        <img
          src="/assets/icons/GuestAccessKeyIcon.png"
          alt="Guest"
          className="view-guest-icon"
        />
      </div>

      <div className="flex-grow-1 ms-2">
        <div style={{ fontSize: 18, fontWeight: 700 }}>{guest?.guest}</div>
        <div className="text-muted" style={{ fontSize: 13 }}>
          {otp}
        </div>
      </div>

      <div className="view-guest-status">
        <Pills
          label={guest?.status ?? "Unknown"}
          variant={
            guest?.status === "Active"
              ? "success"
              : guest?.status === "Revoked" || guest?.status === "Used"
              ? "danger"
              : "ghost"
          }
          size="sm"
        />
      </div>
    </div>
  );

  const footer = (
    <div className="view-guest-footer">
      <div className="footer-actions">
        <Button
          variant="primary"
          // size="full"
          className="w-full mt-4"
          onClick={() => {
            withdrawMutation.mutate(guest.id);
          }}
          loading={withdrawMutation.isPending}
          disabled={withdrawMutation.isPending}
          icon={<img src="/assets/icons/EnterIcon.png" alt="Withdraw" />}
          iconPosition="right"
        >
          Withdraw Access
        </Button>
      </div>
      <div
        className="generate-otp-link w-full"
        style={{
          cursor: generateOtpMutation.isPending ? "not-allowed" : "pointer",
          opacity: generateOtpMutation.isPending ? 0.6 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
        aria-disabled={generateOtpMutation.isPending}
      >
        {generateOtpMutation.isPending && (
          <span
            className="spinner-border spinner-border-sm me-2 text-primary"
            role="status"
            style={{ width: 16, height: 16 }}
          />
        )}
        Generate New O.T.P
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      description={null}
      size="md"
      closable={false}
      className="view-guest-modal"
    >
      {title}
      <div className="view-guest-body">
        <div className="view-guest-active-till">
          <i className="ri-time-line" aria-hidden />
          <span className="view-guest-active-text">
            Active till{" "}
            <span className="view-guest-active-date">
              {formatActiveTill(guest?.end)}
            </span>
          </span>
        </div>

        <div className="view-guest-section">
          <div className="view-guest-section-title">Purpose of Visit</div>
          <div className="view-guest-section-content">
            {guest?.purposeOfVisit}
          </div>
        </div>

        <div className="view-guest-section">
          <div className="view-guest-section-title">Visiting</div>
          <div className="visitor-item">
            <div className="visitor-avatar">
              {guest?.requesteeProfileUrl ? (
                <img
                  src={guest?.requesteeProfileUrl}
                  alt={guest?.requestedBy}
                  className="visitor-avatar-img"
                />
              ) : (
                <span className="visitor-avatar-initial">
                  {guest?.requestedBy?.charAt(0)}
                </span>
              )}
            </div>
            <div className="visitor-info">
              <div className="visitor-name">{guest?.requestedBy}</div>
              {guest?.condo && (
                <div className="visitor-condo">{guest?.condo}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {guest?.status !== "Revoked" && footer}
    </Modal>
  );
};

export default ViewGuestDetailsModal;
